import { getProducts, saveProduct, deleteProduct } from '../../../lib/cms'
import type { Product } from '../../../mocks/products'

const emptyProduct: Product = {
  id: '',
  name: '',
  category: '',
  categorySlug: '',
  shortDesc: '',
  longDesc: '',
  price: '',
  image: '',
  gallery: [],
  specs: [],
  features: [],
  benefits: [],
  isFeatured: false,
}

export default function AdminProducts() {
  const [products, setProducts] = useState<Product[]>([])
  const [loading, setLoading] = useState(true)
  const [editItem, setEditItem] = useState<Product | null>(null)
  const [saving, setSaving] = useState(false)
  const [deleteId, setDeleteId] = useState<string | null>(null)
  const [msg, setMsg] = useState('')

  const load = async () => {
    setLoading(true)
    setProducts(await getProducts())
    setLoading(false)
  }

  useEffect(() => { load() }, [])

  const flash = (m: string) => { setMsg(m); setTimeout(() => setMsg(''), 3000) }

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!editItem) return
    setSaving(true)
    await saveProduct(editItem)
    await load()
    setEditItem(null)
    flash('Proizvod sačuvan!')
    setSaving(false)
  }

  const handleDelete = async () => {
    if (!deleteId) return
    await deleteProduct(deleteId)
    await load()
    setDeleteId(null)
    flash('Proizvod obrisan.')
  }

  const setField = (key: keyof Product, value: any) =>
    setEditItem(prev => prev ? { ...prev, [key]: value } : prev)

  // Array field editors
  const updateArrayField = (key: 'features' | 'gallery', idx: number, value: string) => {
    if (!editItem) return
    const arr = [...(editItem[key] as string[])]
    arr[idx] = value
    setField(key, arr)
  }
  const addArrayItem = (key: 'features' | 'gallery') => {
    if (!editItem) return
    setField(key, [...(editItem[key] as string[]), ''])
  }
  const removeArrayItem = (key: 'features' | 'gallery', idx: number) => {
    if (!editItem) return
    setField(key, (editItem[key] as string[]).filter((_, i) => i !== idx))
  }

  const updateSpec = (idx: number, field: 'label' | 'value', value: string) => {
    if (!editItem) return
    const specs = [...editItem.specs]
    specs[idx] = { ...specs[idx], [field]: value }
    setField('specs', specs)
  }

  return (
    <div className="space-y-4">
      {msg && <div className="bg-green-50 border border-green-200 text-green-800 rounded-lg px-4 py-2 text-sm">{msg}</div>}

      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-gray-500 text-sm">{products.length} proizvoda</p>
        <button
          onClick={() => setEditItem({ ...emptyProduct, id: `product-${Date.now()}` })}
          className="bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium px-4 py-2 rounded-lg flex items-center gap-2 transition"
        >
          <i className="ri-add-line" /> Dodaj proizvod
        </button>
      </div>

      {/* Products table */}
      {loading ? (
        <div className="text-center py-12 text-gray-400"><i className="ri-loader-4-line animate-spin text-2xl" /></div>
      ) : (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-gray-600 font-medium">Naziv</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium hidden md:table-cell">Kategorija</th>
                <th className="text-left px-4 py-3 text-gray-600 font-medium hidden sm:table-cell">Cena</th>
                <th className="text-center px-4 py-3 text-gray-600 font-medium hidden sm:table-cell">Featured</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {products.map(p => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {p.image && (
                        <img src={p.image} alt="" className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                      )}
                      <div>
                        <p className="font-medium text-gray-900">{p.name}</p>
                        <p className="text-gray-400 text-xs truncate max-w-xs">{p.shortDesc}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{p.category}</td>
                  <td className="px-4 py-3 text-gray-600 hidden sm:table-cell">{p.price}</td>
                  <td className="px-4 py-3 text-center hidden sm:table-cell">
                    {p.isFeatured
                      ? <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full"><i className="ri-star-fill" />Da</span>
                      : <span className="text-gray-300 text-xs">—</span>
                    }
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => setEditItem(p)}
                        className="text-gray-400 hover:text-blue-600 transition p-1"
                        title="Uredi"
                      >
                        <i className="ri-edit-line" />
                      </button>
                      <button
                        onClick={() => setDeleteId(p.id)}
                        className="text-gray-400 hover:text-red-500 transition p-1"
                        title="Obriši"
                      >
                        <i className="ri-delete-bin-line" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit Modal */}
      {editItem && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl w-full max-w-2xl my-4">
            <div className="flex items-center justify-between p-6 border-b">
              <h2 className="font-semibold text-gray-900">
                {editItem.id && products.some(p => p.id === editItem.id) ? 'Uredi proizvod' : 'Novi proizvod'}
              </h2>
              <button onClick={() => setEditItem(null)} className="text-gray-400 hover:text-gray-600">
                <i className="ri-close-line text-xl" />
              </button>
            </div>
            <form onSubmit={handleSave} className="p-6 space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Naziv *</label>
                  <input className="input" value={editItem.name} onChange={e => setField('name', e.target.value)} required />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategorija</label>
                  <input className="input" value={editItem.category} onChange={e => setField('category', e.target.value)} placeholder="npr. Kamere" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kategorija slug</label>
                  <input className="input" value={editItem.categorySlug} onChange={e => setField('categorySlug', e.target.value)} placeholder="npr. kamere" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Cena</label>
                  <input className="input" value={editItem.price} onChange={e => setField('price', e.target.value)} placeholder="npr. 34,990 RSD" />
                </div>
                <div className="flex items-center gap-2 pt-6">
                  <input type="checkbox" id="featured" checked={editItem.isFeatured} onChange={e => setField('isFeatured', e.target.checked)} className="w-4 h-4 text-blue-600" />
                  <label htmlFor="featured" className="text-sm text-gray-700">Featured proizvod</label>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">URL slike</label>
                  <input className="input" value={editItem.image} onChange={e => setField('image', e.target.value)} placeholder="https://..." />
                  {editItem.image && <img src={editItem.image} alt="" className="mt-2 h-20 rounded-lg object-cover" />}
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Kratki opis</label>
                  <textarea className="input" rows={2} value={editItem.shortDesc} onChange={e => setField('shortDesc', e.target.value)} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Dugačak opis</label>
                  <textarea className="input" rows={4} value={editItem.longDesc} onChange={e => setField('longDesc', e.target.value)} />
                </div>
              </div>

              {/* Specs */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Specifikacije</label>
                  <button type="button" onClick={() => setField('specs', [...editItem.specs, { label: '', value: '' }])} className="text-blue-600 text-xs hover:underline">+ Dodaj</button>
                </div>
                <div className="space-y-2">
                  {editItem.specs.map((s, i) => (
                    <div key={i} className="flex gap-2">
                      <input className="input flex-1" placeholder="Label" value={s.label} onChange={e => updateSpec(i, 'label', e.target.value)} />
                      <input className="input flex-1" placeholder="Vrednost" value={s.value} onChange={e => updateSpec(i, 'value', e.target.value)} />
                      <button type="button" onClick={() => setField('specs', editItem.specs.filter((_, j) => j !== i))} className="text-red-400 hover:text-red-600 px-2"><i className="ri-delete-bin-line" /></button>
                    </div>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="text-sm font-medium text-gray-700">Karakteristike (lista)</label>
                  <button type="button" onClick={() => addArrayItem('features')} className="text-blue-600 text-xs hover:underline">+ Dodaj</button>
                </div>
                <div className="space-y-2">
                  {editItem.features.map((f, i) => (
                    <div key={i} className="flex gap-2">
                      <input className="input flex-1" value={f} onChange={e => updateArrayField('features', i, e.target.value)} />
                      <button type="button" onClick={() => removeArrayItem('features', i)} className="text-red-400 hover:text-red-600 px-2"><i className="ri-delete-bin-line" /></button>
                    </div>
                  ))}
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setEditItem(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Otkaži</button>
                <button type="submit" disabled={saving} className="bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-6 py-2 rounded-lg transition">
                  {saving ? 'Čuvanje...' : 'Sačuvaj'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete confirm */}
      {deleteId && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full">
            <h3 className="font-semibold text-gray-900 mb-2">Obriši proizvod?</h3>
            <p className="text-gray-500 text-sm mb-5">Ova akcija je trajna i ne može se poništiti.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleteId(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Otkaži</button>
              <button onClick={handleDelete} className="bg-red-600 hover:bg-red-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition">Obriši</button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
